import React, { useEffect, useState } from "react";
import { Route, Switch, useHistory } from "react-router-dom";

import { Security, SecureRoute, LoginCallback } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";

import LogCapture from "./utils/LogManagement/LogHelper";

import Admin from "./layouts/Admin";
import Auth from "./layouts/Auth";
import LoginCustomCallback from "./views/Auth/LoginCustomCallback";
import "./assets/CSS/index.css";
import "./assets/CSS/home.css";

import "./assets/CSS/shared.css";
import "./assets/CSS/sidemenu.css";
import "./assets/CSS/navbar.css";

import "./assets/CSS/users.css";
import "./assets/CSS/users-form.css";
import "./assets/CSS/profile.css";

import "./assets/CSS/model.css";
import "./assets/CSS/modelform.css";

import "./assets/CSS/lab-store.css";
import "./assets/CSS/labstore-product.css";

import "./assets/CSS/scan.css";

import { oktaClient } from "./config/okta.config";
import { getItem } from "./utils/StorageHelpers";
import UserContext from "./lib/UserContext";
import {
    firebaseSignInWithCustomToken,
    getFirebaseToken,
    onMessageListener,
    onSnapshotListener,
} from "./utils/FirebaseHelper";
import { useTranslation } from "react-i18next";

// Google Anayltics
import ReactGA from "react-ga";

import * as Sentry from "@sentry/browser";
import FakePrivacyPolicy from "./views/Admin/FakePrivacyPolicy";
import View from "./layouts/View";
import LicenseExpirationView from "./views/Admin/LicenseExpirationView";
import { getToken } from "./utils/OktaHelpers";

const TRACKING_ID = process.env.REACT_APP_TRACKING_ID || "UA-210560378-1";

const App = () => {
    const history = useHistory();
    const { i18n } = useTranslation();
    const [, setUser] = useState(null);

    useEffect(() => {
        // to ensure having the right pathname
        if (window?.location?.pathname.includes("admin")) {
            window?.location.replace(`${window?.location?.origin}/dashboard`);
        }
        oktaClient.start();
        // get user from localStorage
        const _user = JSON.parse(getItem("user"));
        setUser(_user);

        initReactGA(_user);

        if (getToken()) {
            initFirebase(_user?.email);
        } else {
            setTimeout(() => {
                initFirebase(_user?.email);
            }, 1200);
        }

        return () => {
            oktaClient.stop();
        };
    }, []);

    const initFirebase = async (email) => {
        try {
            await firebaseSignInWithCustomToken(email);
            await getFirebaseToken();

            onMessageListener().catch((error) =>
                LogCapture.error(
                    `Failed to initialize notification center user email: ${email} error: ${error}`
                )
            );

            onSnapshotListener();
        } catch (error) {
            LogCapture.error(
                `Failed to initialize notification center user email: ${email} error: ${error}`
            );
        }
    };

    const initReactGA = (_user) => {
        Sentry.setUser({ email: _user?.email || "Unknow" });

        ReactGA.initialize(TRACKING_ID, {
            gaOptions: {
                userId: _user?.id || "Unknown",
                user: _user?.email || "Unknown",
            },
            cookieDomain: "auto",
        });

        history.listen((location) => {
            ReactGA.pageview(location?.pathname);
        });
    };

    const restoreOriginalUri = async (_oktaAuth, originalUri) => {
        if (originalUri) {
            history.replace(
                toRelativeUrl(originalUri, window?.location?.origin)
            );
        } else {
            history.replace("/dashboard");
        }
    };

    const customAuthHandler = () => {
        history.push("/auth/login");
    };

    const changeLanguage = (lng) => {
        if (i18n.language !== lng) {
            i18n.changeLanguage(lng);
        }
    };

    return (
        <UserContext.Provider
            value={{
                getUser: () => JSON.parse(getItem("user")),
                initFirebase,
                changeLanguage,
            }}
        >
            <Security
                oktaAuth={oktaClient}
                restoreOriginalUri={restoreOriginalUri}
                onAuthRequired={customAuthHandler}
            >
                <Switch>
                    {/* add routes with layouts */}
                    <Route
                        path="/fake-privacy-policy"
                        component={FakePrivacyPolicy}
                    />
                    <SecureRoute path="/view" component={View} />
                    <Route path="/auth" component={Auth} />
                    <Route path="/login/callback" component={LoginCallback} />
                    <Route
                        path="/login/customcallback"
                        component={LoginCustomCallback}
                    />
                    <Route
                        path="/license-expired"
                        component={LicenseExpirationView}
                    />
                    <SecureRoute path="/" component={Admin} />
                </Switch>
            </Security>
        </UserContext.Provider>
    );
};

export default App;
